import React, { useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import SessionDetails from './SessionDetails';
import { formatDateTime } from '../../../Utils/dateFormat';
import NotePdfHeader, { buildNotePdfHeader } from './NotePdfHeader';

// US Letter at 96dpi (8.5in = 816px), same page as the backend note PDF.
const RENDER_WIDTH = 816;
const SCALE = 2;
// Browsers cap canvas dimensions (~16k px is the safe cross-browser side
// limit); past it drawing silently fails and the canvas comes back blank,
// which turns a long report into empty white PDF pages. Capture in slices
// that stay under this limit instead of one giant snapshot.
const MAX_CANVAS_SIDE = 16384;

/**
 * Captures a rendered audit DOM node and its clipping wrapper into a jsPDF
 * document, appending AI generations and MIPS as native text pages, and
 * returns the result as a Uint8Array.
 *
 * Called by ReportPdf (standalone audit button) and will be called by the
 * combined Note+Audit flow (TRPA-8102) once that subtask is implemented.
 *
 * @param {{ node: HTMLElement, clip: HTMLElement, session: object, aiGenerations: object[] }} opts
 * @returns {Promise<Uint8Array>}
 */
export async function generateAuditPdfBytes({ node, clip, session, aiGenerations = [] }) {
  const pdf = new jsPDF('p', 'mm', 'letter');
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  // 0.6in top/bottom page margin, matching the backend note PDF.
  const marginY = 15.24; // mm

  // One PDF page's worth of content, in CSS px of the render node.
  // Subtract the top+bottom margin so the image never reaches the page edge.
  const pageCssPx = Math.floor((RENDER_WIDTH * (pageH - marginY * 2)) / pageW);
  const pagesPerSlice = Math.max(
    1,
    Math.floor(MAX_CANVAS_SIDE / (pageCssPx * SCALE))
  );
  const sliceCssPx = pagesPerSlice * pageCssPx;
  const totalCssPx = node.scrollHeight;

  // Keep only the off-screen report (its subtree + ancestor chain) in
  // html2canvas's document clone; cloning and laying out the whole
  // dashboard behind it for every slice is what makes captures crawl.
  const ignoreElements = el =>
    document.body.contains(el) &&
    !clip.contains(el) &&
    !el.contains(clip);

  let cssOffset = 0;
  let pageIndex = 0;
  while (cssOffset < totalCssPx) {
    // Shift the content up inside the fixed-height clipping wrapper so
    // html2canvas only ever snapshots a safe-sized window of the report.
    const sliceH = Math.min(sliceCssPx, totalCssPx - cssOffset);
    clip.style.height = `${sliceH}px`;
    node.style.marginTop = `-${cssOffset}px`;
    const canvas = await html2canvas(clip, {
      scale: SCALE,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: RENDER_WIDTH,
      ignoreElements
    });

    const pxPerMm = canvas.width / pageW;
    const pagePx = Math.floor((pageCssPx * canvas.width) / RENDER_WIDTH);
    const minPagePx = Math.floor(pagePx * 0.5);

    // Read pixel data once per canvas slice for blank-row detection.
    let pixels = null;
    try {
      pixels = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
    } catch (e) {
      pixels = null;
    }
    const sampleStep = Math.max(4, Math.floor(canvas.width / 200));
    const isBlankRow = y => {
      if (!pixels) return false;
      const rowStart = y * canvas.width * 4;
      for (let x = 0; x < canvas.width; x += sampleStep) {
        const i = rowStart + x * 4;
        if (pixels[i] < 245 || pixels[i + 1] < 245 || pixels[i + 2] < 245) return false;
      }
      return true;
    };

    let slicePos = 0;
    while (slicePos < canvas.height) {
      let chunkPx = Math.min(pagePx, canvas.height - slicePos);
      // Skip sub-pixel rounding slivers that would add a blank page.
      if (chunkPx < 8 && pageIndex > 0) break;
      // Back up to the nearest blank row so headings/text are never split
      // across a page boundary.
      if (slicePos + chunkPx < canvas.height && pixels) {
        let cut = slicePos + chunkPx;
        const limit = slicePos + minPagePx;
        while (cut > limit && !isBlankRow(cut)) cut -= 1;
        if (cut > limit) chunkPx = cut - slicePos;
      }
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = chunkPx;
      const ctx = pageCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(
        canvas,
        0,
        slicePos,
        canvas.width,
        chunkPx,
        0,
        0,
        canvas.width,
        chunkPx
      );
      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(
        pageCanvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        0,
        marginY,
        pageW,
        chunkPx / pxPerMm
      );
      slicePos += chunkPx;
      pageIndex += 1;
    }
    cssOffset += sliceH;
    // Yield so the tab can paint/GC between heavy captures.
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  // The AI generation prompts/responses (and the MIPS block, which must
  // follow them) are appended as native text pages: pushing hundreds of
  // KB of prompt text through html2canvas takes minutes (its
  // line-breaking re-runs per slice), while jsPDF text is instant,
  // selectable and keeps the file small.
  const mips = session?.mips;
  if (aiGenerations.length > 0 || mips) {
    const margin = 15;
    const maxW = pageW - margin * 2;
    const bottom = pageH - margin;
    pdf.addPage();
    let y = margin;
    const ensureRoom = h => {
      if (y + h > bottom) {
        pdf.addPage();
        y = margin;
      }
    };
    const writeLines = (text, size, style) => {
      pdf.setFont('helvetica', style);
      pdf.setFontSize(size);
      const lineH = size * 0.3528 * 1.35; // pt -> mm, plus leading
      const lines = pdf.splitTextToSize(String(text || ''), maxW);
      lines.forEach(line => {
        ensureRoom(lineH);
        pdf.text(line, margin, y);
        y += lineH;
      });
    };
    const writeHeading = text => {
      ensureRoom(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.text(text, pageW / 2, y + 2, { align: 'center' });
      y += 10;
    };

    if (aiGenerations.length > 0) {
      writeHeading('AI Generated Sections');
      aiGenerations.forEach((item, i) => {
        ensureRoom(16);
        writeLines(
          `${i + 1}. ${item.fullName || ''}  -  ${
            item.time ? formatDateTime(item.time) : ''
          }`,
          10,
          'bold'
        );
        y += 1;
        writeLines('Prompt Request:', 9, 'bold');
        writeLines(
          item.request?.find(r => r.role === 'user')?.content ||
            item.promptRequest ||
            'No prompt stored/available',
          8.5,
          'normal'
        );
        y += 2;
        writeLines('Response:', 9, 'bold');
        writeLines(item.response || '', 8.5, 'normal');
        y += 6;
      });
    }

    if (mips) {
      writeHeading('MIPS Score Generation');
      writeLines(
        `${mips.fullName || ''}  -  ${
          mips.timestamp ? formatDateTime(mips.timestamp) : ''
        }`,
        10,
        'bold'
      );
      y += 1;
      writeLines(`Score: ${mips.score}/100`, 9, 'bold');
      y += 2;
      writeLines('Prompt:', 9, 'bold');
      writeLines(mips.prompt || '', 8.5, 'normal');
      y += 2;
      writeLines('Suggestions:', 9, 'bold');
      writeLines(mips.suggestions || '', 8.5, 'normal');
    }
  }

  return new Uint8Array(pdf.output('arraybuffer'));
}

/**
 * Headless generator for the "Patient Report" (Audit/SessionDetails) — the same
 * content as the notes-board sidebar. Renders the report off-screen with the
 * given printOption ('audit' | 'note' | 'combined'), waits for its self-fetch
 * to settle, snapshots it with html2canvas, builds a paged PDF with jsPDF, and
 * loads the blob into the pre-opened `targetWindow`.
 *
 * TODO: remove the printOption='combined' path once the combined Note+Audit
 * flow subtask (TRPA-8102) is complete.
 */
const S = {
  page: { width: RENDER_WIDTH, background: '#fff', padding: '0 72px', color: '#525E6F', fontFamily: "'Krub', sans-serif", fontSize: '11.5px', lineHeight: 1.5, boxSizing: 'border-box' }
};

const ReportPdf = ({
  session,
  printOption,
  header,
  patient,
  aiGenerations = [],
  targetWindow,
  onComplete
}) => {
  const ref = useRef(null);
  const clipRef = useRef(null);
  const doneRef = useRef(false);

  const fail = message => {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.document.open();
      targetWindow.document.write(`
        <!doctype html><html><head><title>Report</title>
        <style>
          html,body{margin:0;height:100%}
          body{display:flex;align-items:center;justify-content:center;
            font-family:'Krub',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
            background:#F0F2F5;color:#b3392f;padding:24px;text-align:center}
          p{font-size:14px;font-weight:500}
        </style></head>
        <body><p>${message}</p></body></html>
      `);
      targetWindow.document.close();
    }
    onComplete && onComplete();
  };

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    const maxTries = 24; // poll ~ up to 8s before capturing anyway

    const capture = async () => {
      if (cancelled || doneRef.current) return;
      doneRef.current = true;
      // Let the final paint settle before snapshotting.
      await new Promise(resolve => setTimeout(resolve, 300));
      const node = ref.current;
      const clip = clipRef.current;
      if (!node || !clip) return fail('Failed to render the report.');
      try {
        const bytes = await generateAuditPdfBytes({ node, clip, session, aiGenerations });
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        if (targetWindow && !targetWindow.closed) {
          targetWindow.location.href = url;
        } else {
          window.open(url, '_blank', 'noopener');
        }
      } catch (e) {
        console.error('Report PDF generation failed:', e);
        return fail('Failed to build the report PDF.');
      } finally {
        if (ref.current) ref.current.style.marginTop = '';
        if (clipRef.current) clipRef.current.style.height = '';
      }
      onComplete && onComplete();
    };

    // SessionDetails sets data-pdf-ready="true" on its root element once all
    // three fetch groups (audit data, demographics+patient, sections) have
    // settled. Poll for that sentinel so we never snapshot a half-loaded doc.
    const tick = () => {
      if (cancelled || doneRef.current) return;
      const node = ref.current;
      const ready = node && node.dataset.pdfReady === 'true';
      tries += 1;
      if (ready || tries >= maxTries) capture();
      else setTimeout(tick, 300);
    };

    const timer = setTimeout(tick, 700);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        left: -10000,
        top: 0,
        width: RENDER_WIDTH,
        background: '#ffffff',
        zIndex: -1,
        pointerEvents: 'none'
      }}
    >
      <div
        ref={clipRef}
        style={{ width: RENDER_WIDTH, background: '#fff', overflow: 'hidden' }}
      >
        <div
          ref={ref}
          style={{ width: RENDER_WIDTH, background: '#fff' }}
        >
          {/* Audit PDF header */}
          <div style={S.page}>
            <NotePdfHeader
              fields={buildNotePdfHeader({ header, patient, session })}
            />
          </div>

          <div style={{ padding: '0 72px', marginTop: 16 }}>
            <SessionDetails
              session={session}
              selectedTab={1}
              printOption={printOption}
              hideAiGenerations
              hideDemographicsHeader
              searchValue=''
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPdf;
